import {
  Flex,
  Menu,
  MenuButton,
  Portal,
  MenuList,
  MenuItem,
  MenuDivider,
  Text,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { defaultIconSize, Icons } from '../shared/Icons';
import { ArchivedTooltip } from '../shared/tooltip/ArchivedTooltip';
import { UserType, Role, UserListItem } from '@texas/api/endpoints/userApi';
import { Branch } from '@texas/api/endpoints/branchesApi';
import { RoleRestrict } from '../shared/RoleRestrict';
import {
  userTypeFriendlyName,
  roleFriendlyName,
} from '@texas/utils/helpers/roleHelpers';
import { convertToEnum } from '@texas/utils/helpers/enumHelpers';
import { UserNameCell } from './UserNameCell';

export const usersColumns = (
  t: TFunction,
  onArchiveOpen: (user: UserListItem) => void,
  onRestore: (user: UserListItem) => Promise<void>,
  onEditOpen: (id: number) => void,
  onRoleOpen: (id: number) => void,
  onWelcomeSent: (id: number) => void,
  onLogoutDevices: (id: number) => Promise<void>,
  branches: Branch[],
) => {
  const columnHelper = createColumnHelper<UserListItem>();
  const columns = [
    columnHelper.accessor('name', {
      cell: (info) => (
        <UserNameCell
          branchIdentifier={
            info.row.original.branchIdentifier ??
            branches.find((branch) => branch.name === info.row.original.branch)
              ?.identifier ??
            null
          }
          id={info.row.original.id}
          name={info.getValue()}
        />
      ),
      header: t('general.user'),
      size: 250,
      minSize: 220,
    }),
    columnHelper.accessor('email', {
      cell: (info) => info.getValue(),
      header: t('general.email'),
      size: 270,
      minSize: 230,
    }),
    columnHelper.accessor('branch', {
      header: t('general.branch'),
      cell: (info) => info.getValue() ?? '—',
      size: 154,
      minSize: 130,
    }),
    columnHelper.accessor('role', {
      cell: (info) => roleFriendlyName(convertToEnum(Role, info.getValue()), t),
      header: t('auth.role'),
      size: 168,
      minSize: 145,
    }),
    columnHelper.accessor('userType', {
      cell: (info) => (
        <Flex direction="column">
          <Text>
            {userTypeFriendlyName(convertToEnum(UserType, info.getValue()), t)}
          </Text>
          {info.row.original.customerName && (
            <Text variant="sub">{info.row.original.customerName}</Text>
          )}
        </Flex>
      ),
      header: t('user.userType.userType'),
      size: 160,
      minSize: 145,
    }),
    columnHelper.accessor('articleCount', {
      header: t('general.articles'),
      size: 92,
      minSize: 82,
    }),
    columnHelper.accessor('customerCount', {
      header: t('general.customers'),
      size: 100,
      minSize: 90,
    }),
    columnHelper.accessor('lastActivity', {
      cell: (info) => info.getValue(),
      header: t('customerUser.lastActivity'),
      enableSorting: false,
      size: 108,
      minSize: 100,
    }),
    columnHelper.accessor('id', {
      cell: (info) => (
        <>
          <Flex justifyContent="end" alignItems="center" gap={4}>
            {info.row.original.archived && (
              <ArchivedTooltip archived={info.row.original.archived} />
            )}
            <RoleRestrict allow={[Role.SystemAdministrator]}>
              <Menu placement="left">
                <MenuButton float="right" aria-label={t('general.more')}>
                  <Icons.DotsHorizontal boxSize={6} />
                </MenuButton>
                <Portal>
                  <MenuList zIndex={99}>
                    {info.row.original.archived ? (
                      <MenuItem
                        color="texas.sand.100"
                        icon={<Icons.Undo boxSize={defaultIconSize} />}
                        onClick={() => onRestore(info.row.original)}
                      >
                        {t('general.restore')}
                      </MenuItem>
                    ) : (
                      <>
                        <MenuItem
                          icon={<Icons.Pencil boxSize={defaultIconSize} />}
                          onClick={() => onEditOpen(info.row.original.id)}
                        >
                          {t('general.edit')}
                        </MenuItem>
                        <MenuItem
                          color="texas.sand.50"
                          icon={<Icons.Archive boxSize={defaultIconSize} />}
                          onClick={() => onArchiveOpen(info.row.original)}
                        >
                          {t('general.archive')}
                        </MenuItem>{' '}
                        <MenuDivider />
                        <MenuItem
                          icon={<Icons.EmailFast boxSize={defaultIconSize} />}
                          onClick={() => onWelcomeSent(info.row.original.id)}
                        >
                          {t('user.sendWelcomeEmail')}
                        </MenuItem>
                        <MenuItem
                          icon={<Icons.ShieldEdit boxSize={defaultIconSize} />}
                          onClick={() => onRoleOpen(info.row.original.id)}
                        >
                          {t('user.changeRole')}
                        </MenuItem>
                      </>
                    )}
                    <MenuItem
                      icon={
                        <Icons.CellphoneLinkOff boxSize={defaultIconSize} />
                      }
                      onClick={() => onLogoutDevices(info.row.original.id)}
                    >
                      {t('user.signOutDevices')}
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
            </RoleRestrict>
          </Flex>
        </>
      ),
      header: '',
      enableSorting: false,
      size: 48,
      maxSize: 48,
    }),
  ];

  return columns;
};
